import React, { useState, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ImageCropper from "../components/ImageCropper";
import api from "../lib/api";
import {
  Store, MapPin, Phone, Mail, Clock, Calendar, Image, Upload,
  Shield, FileText, CreditCard, Send, ChevronLeft, ChevronRight,
  Plus, Trash2, Check, X, Info, ToggleLeft, ToggleRight, Copy,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { key: "monday", label: "Lundi" },
  { key: "tuesday", label: "Mardi" },
  { key: "wednesday", label: "Mercredi" },
  { key: "thursday", label: "Jeudi" },
  { key: "friday", label: "Vendredi" },
  { key: "saturday", label: "Samedi" },
  { key: "sunday", label: "Dimanche" },
];

const DEFAULT_HOURS = () => {
  const h = {};
  DAYS.forEach((d) => {
    h[d.key] = { closed: d.key === "sunday", slots: [{ open: "10:00", close: "19:00" }] };
  });
  return h;
};

const STEPS = [
  { title: "Informations", icon: Store },
  { title: "Horaires", icon: Clock },
  { title: "Photos", icon: Image },
  { title: "Règles", icon: Shield },
  { title: "Légal", icon: FileText },
];

export default function CreateShopPage({ editMode }) {
  const { selectedShop, shops, fetchShops, user } = useAuth();
  const navigate = useNavigate();
  // In edit mode, target the user's existing shop even if none is "selected"
  // (e.g. a single pending shop). This prevents edits from creating a NEW pending request.
  const editShop = editMode
    ? (selectedShop || (shops || []).find((s) => s.status === 'approved') || (shops || [])[0] || null)
    : null;
  const [step, setStep] = useState(0);
  const [proType, setProType] = useState("shop");
  const [loading, setLoading] = useState(false);
  const contentRef = useRef(null);

  // Form state
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [addressData, setAddressData] = useState({});
  const [addressResults, setAddressResults] = useState([]);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [openingHours, setOpeningHours] = useState(DEFAULT_HOURS());
  const [exceptionalClosures, setExceptionalClosures] = useState([]);
  const [bannerUrl, setBannerUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [description, setDescription] = useState("");
  const [freePlayAllowed, setFreePlayAllowed] = useState(false);
  const [freePlayConditions, setFreePlayConditions] = useState("");
  const [waitlistMode, setWaitlistMode] = useState("manual");
  const [waitlistConfirmMinutes, setWaitlistConfirmMinutes] = useState("30");
  const [refundEnabled, setRefundEnabled] = useState(false);
  const [refundHours, setRefundHours] = useState("24");
  const [legalName, setLegalName] = useState("");
  const [legalPhone, setLegalPhone] = useState("");
  const [legalEmail, setLegalEmail] = useState("");
  const [managerName, setManagerName] = useState("");
  const [managerPhone, setManagerPhone] = useState("");
  const [managerEmail, setManagerEmail] = useState("");
  const [iban, setIban] = useState("");
  const [roomCapacity, setRoomCapacity] = useState("");
  const [instagram, setInstagram] = useState("");
  const [facebook, setFacebook] = useState("");
  const [discord, setDiscord] = useState("");
  const [website, setWebsite] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [visibleInDiscover, setVisibleInDiscover] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [cropper, setCropper] = useState(null); // { file, type, aspect, title, outputWidth }

  // Load existing shop data in edit mode
  React.useEffect(() => {
    if (editMode && editShop) {
      const selectedShop = editShop;
      setName(selectedShop.name || '');
      setProType(selectedShop.pro_type || 'shop');
      setAddress(selectedShop.address || '');
      setPhone(selectedShop.phone || '');
      setEmail(selectedShop.email || '');
      if (selectedShop.opening_hours) setOpeningHours(selectedShop.opening_hours);
      setBannerUrl(selectedShop.banner_url || '');
      setLogoUrl(selectedShop.logo_url || '');
      setDescription(selectedShop.description || '');
      setFreePlayAllowed(selectedShop.free_play_allowed || false);
      setFreePlayConditions(selectedShop.free_play_conditions || '');
      setWaitlistMode(selectedShop.waitlist_mode || 'manual');
      setWaitlistConfirmMinutes(String(selectedShop.waitlist_confirm_minutes || 30));
      setRefundEnabled(selectedShop.refund_policy?.enabled || false);
      setRefundHours(String(selectedShop.refund_policy?.hours_before || 24));
      setLegalName(selectedShop.legal_representative?.name || '');
      setLegalPhone(selectedShop.legal_representative?.phone || '');
      setLegalEmail(selectedShop.legal_representative?.email || '');
      setManagerName(selectedShop.shop_manager?.name || '');
      setManagerPhone(selectedShop.shop_manager?.phone || '');
      setManagerEmail(selectedShop.shop_manager?.email || '');
      setAddressData(selectedShop.address_data || {});
      setIban(selectedShop.iban || '');
      setRoomCapacity(selectedShop.room_capacity || '');
      setInstagram(selectedShop.instagram || '');
      setFacebook(selectedShop.facebook || '');
      setDiscord(selectedShop.discord || '');
      setWebsite(selectedShop.website || '');
      setWhatsapp(selectedShop.whatsapp || '');
      setVisibleInDiscover(selectedShop.visible_in_discover !== false);
      setIsActive(selectedShop.is_active !== false);
    }
  }, [editMode, editShop]);


  const searchAddress = useCallback(async (q) => {
    setAddress(q);
    if (q.length < 4) { setAddressResults([]); return; }
    try {
      const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=5`);
      const data = await res.json();
      setAddressResults(
        (data.features || []).map((f) => ({
          label: f.properties.label,
          housenumber: f.properties.housenumber,
          street: f.properties.street,
          postcode: f.properties.postcode,
          city: f.properties.city,
          context: f.properties.context,
          x: f.geometry?.coordinates?.[0],
          y: f.geometry?.coordinates?.[1],
        }))
      );
    } catch {
      setAddressResults([]);
    }
  }, []);

  const uploadImage = (type) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 20 * 1024 * 1024) {
        toast.error("Fichier trop lourd (max 20 Mo)");
        return;
      }
      setCropper(
        type === "logo"
          ? { file, type: "logo", aspect: 1, circular: true, title: "Recadrer le logo (rond)", outputWidth: 512 }
          : { file, type: "banner", aspect: 3, circular: false, title: "Recadrer la bannière (3:1)", outputWidth: 1200 }
      );
    };
    input.click();
  };

  const handleCropped = async (blob) => {
    const type = cropper?.type;
    setCropper(null);
    if (!blob || !type) return;
    const formData = new FormData();
    formData.append("file", blob, `${type}.jpg`);
    try {
      const res = await api.post("/upload/shop-asset", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res.data;
      if (type === "logo") setLogoUrl(data.url);
      else setBannerUrl(data.url);
      if (data.storage_warning) toast.warning(data.storage_warning);
      toast.success(`${type === "logo" ? "Logo" : "Bannière"} uploadé (${data.size_kb?.toFixed(0)} KB)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Erreur upload");
    }
  };

  const updateDayHours = (day, field, value) => {
    setOpeningHours((prev) => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };
  const updateSlot = (day, idx, field, value) => {
    setOpeningHours((prev) => {
      const slots = [...(prev[day]?.slots || [])];
      slots[idx] = { ...slots[idx], [field]: value };
      return { ...prev, [day]: { ...prev[day], slots } };
    });
  };
  const addSlot = (day) => {
    setOpeningHours((prev) => {
      const slots = [...(prev[day]?.slots || [])];
      if (slots.length < 3) slots.push({ open: "14:00", close: "18:00" });
      return { ...prev, [day]: { ...prev[day], slots } };
    });
  };
  const removeSlot = (day, idx) => {
    setOpeningHours((prev) => {
      const slots = (prev[day]?.slots || []).filter((_, i) => i !== idx);
      return { ...prev, [day]: { ...prev[day], slots } };
    });
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Nom requis");
    if (!email.trim()) return toast.error("Email requis");
    if (!logoUrl) return toast.error("Logo obligatoire");
    if (!legalName.trim() || !legalEmail.trim()) return toast.error("Représentant légal requis");
    if (freePlayAllowed && !freePlayConditions.trim()) return toast.error("Conditions jeu libre requises");

    const body = {
      name: name.trim(),
      pro_type: proType,
      address: address.trim(),
      address_data: addressData,
      phone: phone.trim(),
      email: email.trim(),
      opening_hours: isShop ? openingHours : null,
      exceptional_closures: isShop ? exceptionalClosures.filter((c) => c.date) : [],
      banner_url: bannerUrl || null,
      logo_url: logoUrl,
      description: description.trim(),
      free_play_allowed: isShop ? freePlayAllowed : false,
      free_play_conditions: (isShop && freePlayAllowed) ? freePlayConditions.trim() : null,
      waitlist_mode: waitlistMode,
      waitlist_confirm_minutes: parseInt(waitlistConfirmMinutes) || 30,
      refund_policy: { enabled: refundEnabled, hours_before: parseInt(refundHours) || 24 },
      legal_representative: { name: legalName.trim(), phone: legalPhone.trim(), email: legalEmail.trim() },
      shop_manager: managerName.trim() ? { name: managerName.trim(), phone: managerPhone.trim(), email: managerEmail.trim() } : null,
      iban: iban.trim() || null,
      room_capacity: roomCapacity ? parseInt(roomCapacity) : null,
      instagram: instagram.trim() || null,
      facebook: facebook.trim() || null,
      discord: discord.trim() || null,
      website: website.trim() || null,
      whatsapp: whatsapp.trim() || null,
      visible_in_discover: visibleInDiscover,
      is_active: isActive,
    };

    setLoading(true);
    try {
      const isEdit = editMode && editShop;
      if (isEdit) {
        await api.put(`/shops/${editShop.id}`, body);
        toast.success("Boutique mise à jour !");
        fetchShops();
      } else {
        await api.post(`/shops`, body);
        toast.success("Demande envoyée ! Comptez max 48h ouvrées. Contactez admin@arcanapp.com si besoin.");
      }
      setTimeout(() => navigate(-1), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const canGoNext = () => {
    if (currentKey === "info") return !!name.trim() && !!email.trim();
    if (currentKey === "photos") return !!logoUrl;
    if (currentKey === "legal") return !!legalName.trim() && !!legalEmail.trim();
    return true;
  };

  const scrollTop = () => contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });

  const isShop = proType === "shop";
  const STEP_DEFS = [
    { key: "info", title: "Informations", icon: Store },
    ...(isShop ? [{ key: "hours", title: "Horaires", icon: Clock }] : []),
    { key: "photos", title: "Photos", icon: Image },
    { key: "rules", title: "Conditions", icon: Shield },
    { key: "legal", title: "Légal", icon: FileText },
  ];
  const currentKey = STEP_DEFS[Math.min(step, STEP_DEFS.length - 1)]?.key;

  const PRO_TYPES = [
    { key: "shop", label: "Boutique" },
    { key: "association", label: "Association" },
    { key: "organizer", label: "Organisateur" },
    { key: "publisher", label: "Éditeur" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="create-shop-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#121212]/95 backdrop-blur-sm border-b border-[#333]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-[#B0B0B0] hover:text-white transition-colors" data-testid="create-shop-back">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold">{editMode && selectedShop ? 'Modifier mon espace PRO' : 'Espace PRO'}</h1>
          <div className="w-5" />
        </div>
        {/* Steps */}
        <div className="max-w-3xl mx-auto px-4 pb-3 flex gap-1">
          {STEP_DEFS.map((s, i) => {
            const StepIcon = s.icon;
            return (
              <button key={i} onClick={() => setStep(i)}
                className={`flex-1 flex flex-col items-center gap-1 py-1.5 px-1 rounded-lg transition-colors text-xs font-semibold ${
                  step === i ? "bg-[#FF6B35]/10 text-[#FF6B35]" : i < step ? "text-[#FF6B35]/60" : "text-[#666]"
                }`}>
                <StepIcon className="w-4 h-4" />
                <span className="hidden sm:block">{s.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div ref={contentRef} className="max-w-3xl mx-auto px-4 py-6 pb-32 overflow-y-auto">

        {/* Public shareable page link (edit mode) */}
        {editMode && editShop?.slug && (
          <div className="mb-6 rounded-xl border border-[#FF6B35]/30 bg-[#FF6B35]/5 p-4" data-testid="shop-public-link">
            <p className="text-sm font-semibold text-white flex items-center gap-2"><Store className="w-4 h-4 text-[#FF6B35]" />Votre page publique</p>
            <p className="text-xs text-[#B0B0B0] mt-1">Partagez ce lien avec vos clients (comme une page Facebook pro).</p>
            <div className="flex items-center gap-2 mt-2">
              <code className="flex-1 truncate text-sm text-[#FF6B35] bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2">{`${window.location.origin}/${editShop.slug}`}</code>
              <button type="button" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/${editShop.slug}`); toast.success('Lien copié !'); }} className="px-3 py-2 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white text-sm font-medium transition-colors" data-testid="copy-public-link">Copier</button>
              <a href={`/${editShop.slug}`} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg bg-[#252525] border border-[#333] text-white text-sm font-medium hover:bg-[#333] transition-colors">Voir</a>
            </div>
          </div>
        )}

        {/* Step: General info */}
        {currentKey === "info" && (
          <div className="space-y-4" data-testid="step-info">
            <div data-testid="pro-type-selector">
              <label className="block text-sm font-semibold text-[#B0B0B0] mb-2">Type de compte PRO *</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PRO_TYPES.map((pt) => (
                  <button key={pt.key} type="button" onClick={() => { setProType(pt.key); setStep(0); }}
                    data-testid={`pro-type-${pt.key}`}
                    className={`py-2.5 px-3 rounded-lg text-sm font-semibold border transition-colors ${proType === pt.key ? "bg-[#FF6B35] border-[#FF6B35] text-white" : "bg-[#1a1a1a] border-[#333] text-[#B0B0B0] hover:border-[#FF6B35]/50"}`}>
                    {pt.label}
                  </button>
                ))}
              </div>
            </div>
            <h2 className="text-xl font-bold flex items-center gap-2"><Store className="w-5 h-5 text-[#FF6B35]" /> Informations générales</h2>
            <div>
              <label className="block text-sm font-semibold text-[#B0B0B0] mb-1">{isShop ? "Nom de la boutique *" : "Nom *"}</label>
              <input data-testid="shop-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Card Haven Paris"
                className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] focus:border-[#FF6B35] outline-none" />
            </div>
            <div className="relative">
              <label className="block text-sm font-semibold text-[#B0B0B0] mb-1">{isShop ? "Adresse *" : "Adresse"}</label>
              <input data-testid="shop-address-input" value={address} onChange={(e) => searchAddress(e.target.value)} placeholder="Rechercher une adresse..."
                className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] focus:border-[#FF6B35] outline-none" />
              {addressResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-[#1a1a1a] border border-[#333] rounded-lg mt-1 z-10 max-h-60 overflow-y-auto">
                  {addressResults.map((r, i) => (
                    <button key={i} data-testid={`address-result-${i}`} onClick={() => { setAddress(r.label); setAddressData(r); setAddressResults([]); }}
                      className="w-full flex items-center gap-2 px-4 py-3 text-left text-sm text-white hover:bg-[#252525] border-b border-[#333]">
                      <MapPin className="w-4 h-4 text-[#FF6B35] flex-shrink-0" /> {r.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#B0B0B0] mb-1">Téléphone (optionnel)</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33..."
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] focus:border-[#FF6B35] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#B0B0B0] mb-1">Email de la boutique *</label>
                <input data-testid="shop-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contact@maboutique.com"
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] focus:border-[#FF6B35] outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#B0B0B0] mb-1">Capacité de la salle (nombre de places)</label>
              <input type="number" min="1" value={roomCapacity} onChange={(e) => setRoomCapacity(e.target.value)} placeholder="Ex: 64" data-testid="shop-room-capacity-input"
                className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] focus:border-[#FF6B35] outline-none" />
              <p className="text-xs text-[#666] mt-1">Sert d'alerte si un tournoi dépasse la capacité de votre salle.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#B0B0B0] mb-1">Instagram</label>
                <input value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="@maboutique ou lien" data-testid="shop-instagram-input"
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] focus:border-[#FF6B35] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#B0B0B0] mb-1">Facebook</label>
                <input value={facebook} onChange={(e) => setFacebook(e.target.value)} placeholder="Page Facebook ou lien" data-testid="shop-facebook-input"
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] focus:border-[#FF6B35] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#B0B0B0] mb-1">Site web</label>
                <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://maboutique.com" data-testid="shop-website-input"
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] focus:border-[#FF6B35] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#B0B0B0] mb-1">Discord</label>
                <input value={discord} onChange={(e) => setDiscord(e.target.value)} placeholder="https://discord.gg/xxxx" data-testid="shop-discord-input"
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] focus:border-[#FF6B35] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#B0B0B0] mb-1">WhatsApp</label>
                <input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="Numéro (ex: +33612345678) ou lien wa.me" data-testid="shop-whatsapp-input"
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] focus:border-[#FF6B35] outline-none" />
              </div>
            </div>
            <div className="flex items-start gap-3 bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
              <input
                type="checkbox"
                id="hide-from-public"
                checked={!visibleInDiscover}
                onChange={(e) => setVisibleInDiscover(!e.target.checked)}
                data-testid="shop-hide-from-public"
                className="mt-1 w-4 h-4 accent-[#FF6B35] cursor-pointer"
              />
              <label htmlFor="hide-from-public" className="cursor-pointer">
                <span className="block text-sm font-semibold text-white">Masquer mes tournois du calendrier public</span>
                <span className="block text-xs text-[#666] mt-0.5">Vos tournois n'apparaîtront pas dans la découverte publique du site (joueurs & visiteurs). Ils restent visibles dans votre widget iframe et votre tableau de bord.</span>
              </label>
            </div>
            {editMode && editShop && (user?.id === editShop.owner_id || user?.role === 'ADMIN') && (
              <div className={`flex items-start gap-3 border rounded-lg p-4 ${isActive ? 'bg-[#1a1a1a] border-[#333]' : 'bg-red-500/10 border-red-500/40'}`} data-testid="shop-deactivate-block">
                <input
                  type="checkbox"
                  id="deactivate-shop"
                  checked={!isActive}
                  onChange={(e) => setIsActive(!e.target.checked)}
                  data-testid="shop-deactivate-toggle"
                  className="mt-1 w-4 h-4 accent-red-500 cursor-pointer"
                />
                <label htmlFor="deactivate-shop" className="cursor-pointer">
                  <span className="block text-sm font-semibold text-white">Désactiver cette boutique</span>
                  <span className="block text-xs text-[#666] mt-0.5">La boutique et tous ses tournois deviennent invisibles publiquement (page boutique, recherche, découverte). Vous pourrez la réactiver à tout moment en décochant cette case. Réservé à l'administrateur de la boutique.</span>
                  {!isActive && <span className="block text-xs text-red-400 font-semibold mt-1">⚠️ Boutique actuellement désactivée — invisible publiquement.</span>}
                </label>
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-[#B0B0B0] mb-1">Description (280 car.)</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, 280))} placeholder="Décrivez votre boutique..." rows={3}
                className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] focus:border-[#FF6B35] outline-none resize-none" />
              <p className="text-xs text-[#666] text-right">{description.length}/280</p>
            </div>
          </div>
        )}

        {/* Step 1: Opening hours */}
        {currentKey === "hours" && (
          <div className="space-y-4" data-testid="step-hours">
            <h2 className="text-xl font-bold flex items-center gap-2"><Clock className="w-5 h-5 text-[#FF6B35]" /> Horaires d'ouverture</h2>
            {DAYS.map((day) => {
              const hours = openingHours[day.key] || { closed: false, slots: [] };
              return (
                <div key={day.key} className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-sm">{day.label}</span>
                    <button onClick={() => updateDayHours(day.key, "closed", !hours.closed)}
                      className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                        hours.closed ? "bg-red-500/20 text-red-400" : "bg-green-500/20 text-green-400"
                      }`}>
                      {hours.closed ? <X className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                      {hours.closed ? "Fermé" : "Ouvert"}
                    </button>
                  </div>
                  {!hours.closed && (
                    <div className="space-y-2">
                      {(hours.slots || []).map((slot, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input value={slot.open} onChange={(e) => updateSlot(day.key, idx, "open", e.target.value)} placeholder="10:00"
                            className="w-20 bg-[#0a0a0a] border border-[#333] rounded px-2 py-1.5 text-center text-sm text-white" />
                          <span className="text-[#666] text-xs">à</span>
                          <input value={slot.close} onChange={(e) => updateSlot(day.key, idx, "close", e.target.value)} placeholder="19:00"
                            className="w-20 bg-[#0a0a0a] border border-[#333] rounded px-2 py-1.5 text-center text-sm text-white" />
                          {idx > 0 && (
                            <button onClick={() => removeSlot(day.key, idx)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                          )}
                        </div>
                      ))}
                      {(hours.slots || []).length < 3 && (
                        <button onClick={() => addSlot(day.key)} className="flex items-center gap-1 text-xs text-[#FF6B35] font-semibold hover:text-[#ff8555]">
                          <Plus className="w-3 h-3" /> Ajouter un créneau
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="mt-6">
              <h3 className="font-bold text-sm text-[#B0B0B0] mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Fermetures exceptionnelles</h3>
              {exceptionalClosures.map((c, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input value={c.date} onChange={(e) => {
                    const upd = [...exceptionalClosures]; upd[i] = { ...upd[i], date: e.target.value }; setExceptionalClosures(upd);
                  }} placeholder="JJ/MM/AAAA" className="bg-[#0a0a0a] border border-[#333] rounded px-3 py-2 text-sm text-white w-36" />
                  <input value={c.reason} onChange={(e) => {
                    const upd = [...exceptionalClosures]; upd[i] = { ...upd[i], reason: e.target.value }; setExceptionalClosures(upd);
                  }} placeholder="Raison / Horaires spéciaux" className="flex-1 bg-[#0a0a0a] border border-[#333] rounded px-3 py-2 text-sm text-white" />
                  <button onClick={() => setExceptionalClosures(exceptionalClosures.filter((_, j) => j !== i))} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={() => setExceptionalClosures([...exceptionalClosures, { date: "", reason: "" }])}
                className="flex items-center gap-2 px-4 py-2 border border-dashed border-[#555] rounded-lg text-sm font-semibold text-[#FF6B35] hover:bg-[#FF6B35]/5 transition-colors">
                <Plus className="w-4 h-4" /> Ajouter une date
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Photos */}
        {currentKey === "photos" && (
          <div className="space-y-6" data-testid="step-photos">
            <h2 className="text-xl font-bold flex items-center gap-2"><Image className="w-5 h-5 text-[#FF6B35]" /> Photos</h2>

            <div>
              <label className="block text-sm font-semibold text-[#B0B0B0] mb-2">Logo * (obligatoire)</label>
              <button onClick={() => uploadImage("logo")} data-testid="upload-logo-btn"
                className={`w-full py-8 rounded-xl border-2 border-dashed ${logoUrl ? "border-green-500/50 bg-green-500/5" : "border-[#555] hover:border-[#FF6B35]"} transition-colors flex flex-col items-center gap-2`}>
                {logoUrl ? (
                  <>
                    <Check className="w-8 h-8 text-green-400" />
                    <span className="text-green-400 font-semibold">Logo uploadé</span>
                    <img src={`${API_URL}${logoUrl}`} alt="Logo" className="w-16 h-16 rounded-full object-cover mt-2 border-2 border-[#FF6B35]" />
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-[#666]" />
                    <span className="text-[#B0B0B0] font-semibold">Choisir un logo</span>
                    <span className="text-xs text-[#666]">Format carré recommandé</span>
                  </>
                )}
              </button>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#B0B0B0] mb-1">Bannière (optionnel)</label>
              <p className="text-xs text-[#666] mb-2">Ratio 3:1 — Photo de salle, devanture, etc.</p>
              <button onClick={() => uploadImage("banner")} data-testid="upload-banner-btn"
                className={`w-full py-6 rounded-xl border-2 border-dashed ${bannerUrl ? "border-green-500/50 bg-green-500/5" : "border-[#555] hover:border-[#FF6B35]"} transition-colors flex flex-col items-center gap-2`}>
                {bannerUrl ? (
                  <>
                    <Check className="w-8 h-8 text-green-400" />
                    <span className="text-green-400 font-semibold">Bannière uploadée</span>
                    <img src={`${API_URL}${bannerUrl}`} alt="Banner" className="w-48 h-16 rounded-lg object-cover mt-2" />
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-[#666]" />
                    <span className="text-[#B0B0B0] font-semibold">Choisir une bannière</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step: Rules */}
        {currentKey === "rules" && (
          <div className="space-y-4" data-testid="step-rules">
            <h2 className="text-xl font-bold flex items-center gap-2"><Shield className="w-5 h-5 text-[#FF6B35]" /> Règles et conditions</h2>

            {/* Free play (boutiques uniquement) */}
            {isShop && (
            <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-sm">Jeu libre autorisé ?</p>
                  <p className="text-xs text-[#666] mt-0.5">Les joueurs pourront annoncer vouloir jouer dans votre boutique</p>
                </div>
                <button onClick={() => setFreePlayAllowed(!freePlayAllowed)}
                  className={`p-1 rounded-full transition-colors ${freePlayAllowed ? "text-green-400" : "text-[#555]"}`}>
                  {freePlayAllowed ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
                </button>
              </div>
              {freePlayAllowed && (
                <div className="mt-3">
                  <label className="block text-xs font-semibold text-[#B0B0B0] mb-1">Conditions * (600 car.)</label>
                  <textarea value={freePlayConditions} onChange={(e) => setFreePlayConditions(e.target.value.slice(0, 600))} rows={3}
                    placeholder="Horaires, tarifs, règles de conduite..."
                    className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-sm text-white placeholder-[#555] outline-none resize-none" />
                  <p className="text-xs text-[#666] text-right">{freePlayConditions.length}/600</p>
                </div>
              )}
            </div>
            )}

            {/* Waitlist mode */}
            <div>
              <h3 className="font-bold text-sm text-[#B0B0B0] mb-3">Gestion des tournois complets (valeur par défaut)</h3>
              {[
                { value: "manual", title: "Liste d'attente manuelle", desc: "Vous contactez les personnes de la liste et envoyez un lien d'inscription, ou les inscrivez manuellement." },
                { value: "auto_push", title: "Notification à tous", desc: "Les intéressés reçoivent une notification push dès qu'une place se libère. Premier arrivé, premier servi." },
                { value: "automatic", title: "Automatique (file séquentielle)", desc: "Le 1er de la file est notifié seul et dispose d'un délai pour confirmer. Sans réponse, la place passe au suivant." },
              ].map((m) => (
                <button key={m.value} onClick={() => setWaitlistMode(m.value)} type="button"
                  className={`w-full flex items-start gap-3 p-4 mb-2 rounded-lg border text-left transition-colors ${
                    waitlistMode === m.value ? "border-[#FF6B35] bg-[#FF6B35]/5" : "border-[#333] bg-[#1a1a1a] hover:border-[#555]"
                  }`} data-testid={`shop-waitlist-${m.value}`}>
                  <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 ${waitlistMode === m.value ? "border-[#FF6B35] bg-[#FF6B35]" : "border-[#555]"}`}>
                    {waitlistMode === m.value && <Check className="w-3 h-3 text-white m-auto" />}
                  </div>
                  <div>
                    <p className="font-bold text-sm">{m.title}</p>
                    <p className="text-xs text-[#888] mt-1 leading-relaxed">{m.desc}</p>
                  </div>
                </button>
              ))}
              {waitlistMode === "automatic" && (
                <div className="flex items-center gap-2 mt-2 bg-[#1a1a1a] border border-[#333] rounded-lg p-3">
                  <span className="text-sm text-[#B0B0B0]">Délai de confirmation par défaut</span>
                  <input value={waitlistConfirmMinutes} onChange={(e) => setWaitlistConfirmMinutes(e.target.value)} type="number" min="5" max="1440"
                    className="w-16 bg-[#0a0a0a] border border-[#333] rounded px-2 py-1.5 text-center text-sm text-white" data-testid="shop-waitlist-minutes" />
                  <span className="text-sm text-[#B0B0B0]">minutes</span>
                </div>
              )}
              <p className="text-xs text-[#666] mt-1">Cette valeur pré-remplit chaque nouvel événement (modifiable au cas par cas).</p>
            </div>

            {/* Refund */}
            <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
              <div className="flex items-center justify-between">
                <p className="font-bold text-sm">Remboursement si annulation ?</p>
                <button onClick={() => setRefundEnabled(!refundEnabled)}
                  className={`p-1 rounded-full transition-colors ${refundEnabled ? "text-green-400" : "text-[#555]"}`}>
                  {refundEnabled ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
                </button>
              </div>
              {refundEnabled && (
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-sm text-[#B0B0B0]">Jusqu'à</span>
                  <input value={refundHours} onChange={(e) => setRefundHours(e.target.value)} type="number" min="1"
                    className="w-16 bg-[#0a0a0a] border border-[#333] rounded px-2 py-1.5 text-center text-sm text-white" />
                  <span className="text-sm text-[#B0B0B0]">heures avant le tournoi</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step: Legal */}
        {currentKey === "legal" && (
          <div className="space-y-4" data-testid="step-legal">
            <h2 className="text-xl font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-[#FF6B35]" /> Informations légales</h2>

            <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 space-y-3">
              <p className="font-bold text-sm">Représentant légal *</p>
              <input data-testid="legal-name-input" value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="Nom complet *"
                className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] outline-none" />
              <div className="grid grid-cols-2 gap-3">
                <input value={legalPhone} onChange={(e) => setLegalPhone(e.target.value)} placeholder="Téléphone"
                  className="bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] outline-none" />
                <input data-testid="legal-email-input" value={legalEmail} onChange={(e) => setLegalEmail(e.target.value)} placeholder="Email *"
                  className="bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] outline-none" />
              </div>
            </div>

            <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="font-bold text-sm">Responsable boutique Arcanapp <span className="text-[#666] font-normal">(si différent)</span></p>
                <button
                  type="button"
                  data-testid="copy-legal-to-manager-btn"
                  disabled={!legalName.trim() && !legalPhone.trim() && !legalEmail.trim()}
                  onClick={() => {
                    setManagerName(legalName);
                    setManagerPhone(legalPhone);
                    setManagerEmail(legalEmail);
                    toast.success("Coordonnées du représentant légal copiées");
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                    (!legalName.trim() && !legalPhone.trim() && !legalEmail.trim())
                      ? "text-[#555] cursor-not-allowed"
                      : "text-[#FF6B35] hover:bg-[#FF6B35]/10"
                  }`}
                >
                  <Copy className="w-3.5 h-3.5" /> Identique au représentant légal
                </button>
              </div>
              <input value={managerName} onChange={(e) => setManagerName(e.target.value)} placeholder="Nom complet"
                className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] outline-none" />
              <div className="grid grid-cols-2 gap-3">
                <input value={managerPhone} onChange={(e) => setManagerPhone(e.target.value)} placeholder="Téléphone"
                  className="bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] outline-none" />
                <input value={managerEmail} onChange={(e) => setManagerEmail(e.target.value)} placeholder="Email"
                  className="bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] outline-none" />
              </div>
            </div>

            <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
              <p className="font-bold text-sm mb-1 flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#FF6B35]" /> IBAN (optionnel)</p>
              <p className="text-xs text-[#666] mb-3">Pour recevoir le montant des inscriptions payantes</p>
              <input value={iban} onChange={(e) => setIban(e.target.value)} placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#555] outline-none font-mono" />
            </div>

            {editMode && editShop ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex gap-3">
              <Info className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-300 leading-relaxed">
                Vos modifications sont <strong>appliquées immédiatement</strong> dès que vous cliquez sur « Sauvegarder ». Aucune validation admin n'est nécessaire.
              </p>
            </div>
            ) : (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex gap-3">
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-300 leading-relaxed">
                Votre demande sera examinée sous <strong>48h ouvrées maximum</strong>. En cas de besoin, contactez directement <strong>admin@arcanapp.com</strong>.
              </p>
            </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#121212]/95 backdrop-blur-sm border-t border-[#333] py-3 px-4 z-20">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          {step > 0 ? (
            <button onClick={() => { setStep(step - 1); scrollTop(); }} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-[#B0B0B0] hover:text-white transition-colors">
              <ChevronLeft className="w-4 h-4" /> Précédent
            </button>
          ) : <div />}
          {step < STEP_DEFS.length - 1 ? (
            <button onClick={() => { setStep(step + 1); scrollTop(); }} disabled={!canGoNext()}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold text-white transition-colors ${canGoNext() ? "bg-[#FF6B35] hover:bg-[#E55A25]" : "bg-[#333] cursor-not-allowed"}`}>
              Suivant <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={!canGoNext() || loading} data-testid="submit-shop-btn"
              className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold text-white transition-colors ${canGoNext() && !loading ? "bg-green-600 hover:bg-green-500" : "bg-[#333] cursor-not-allowed"}`}>
              {loading ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Send className="w-4 h-4" />}
              {editMode && editShop ? 'Sauvegarder' : 'Envoyer la demande'}
            </button>
          )}
        </div>
      </div>

      {cropper && (
        <ImageCropper
          file={cropper.file}
          aspect={cropper.aspect}
          circular={cropper.circular}
          title={cropper.title}
          outputWidth={cropper.outputWidth}
          onCancel={() => setCropper(null)}
          onCropped={handleCropped}
        />
      )}
    </div>
  );
}
